import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';

import { Chip, Typography } from '@zendesk/forethought-elements';
import RecommendationsSection from './RecommendationsSection';
import { getVariantsKey, getVariantVersionLabel } from './utils';
import { insightGradients } from 'src/constants/colors';
import { useGenerateVariantSummariesQuery } from 'src/services/agent-ab-test/agentAbTestApi';
import {
  SolveABTestConfig,
  SolveABTestVariant,
} from 'src/services/agent-ab-test/types';
import { useGetAllAutoflowVersionHistoriesV2Query } from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';
import { SolveWidgetProduct } from 'src/types/types';
import { PRODUCT_TO_CHANNEL_MAP } from 'src/utils/constants';
import { Routes } from 'src/utils/enums';

const SUMMARY_CARD_GRADIENT = insightGradients[15];

const getBadgeColor = (
  variant: SolveABTestVariant,
  index: number,
  colors: { control: string; variantA: string; variantB: string },
) => {
  if (variant.is_control) {
    return colors.control;
  }
  return index === 1 ? colors.variantA : colors.variantB;
};

const getVersionLink = (
  variant: SolveABTestVariant,
  intentWorkflowId: string | null | undefined,
  product: SolveWidgetProduct | undefined,
) => {
  if (!intentWorkflowId) {
    return '';
  }
  const view = product ? PRODUCT_TO_CHANNEL_MAP[product] : 'widget';
  const params = new URLSearchParams({
    version: String(variant.intent_workflow_version),
    versionType: variant.version_type,
    view,
    workflowId: intentWorkflowId,
  });
  return `${Routes.WORKFLOW_BUILDER_EDIT}?${params}`;
};

interface ABTestSidebarProps {
  config: SolveABTestConfig;
}

const ABTestSidebar = ({ config }: ABTestSidebarProps) => {
  const { t } = useTranslation('agent-ab-test');
  const { palette } = useTheme();
  const { data: allVersionHistories } =
    useGetAllAutoflowVersionHistoriesV2Query();

  const validVariants = useMemo(
    () =>
      (config.variants ?? []).filter(
        v => v.intent_workflow_version > 0 && v.name,
      ),
    [config.variants],
  );

  const variantsKey = useMemo(() => getVariantsKey(config), [config]);

  // RTK Query handles loading, caching, and refetching
  const { data: summaries = [], isLoading: isLoadingSummaries } =
    useGenerateVariantSummariesQuery(
      {
        abTestConfigId: config.ab_test_config_id,
        intentWorkflowId: config.intent_workflow_id ?? '',
        variants: validVariants,
        variantsKey,
      },
      {
        skip: !validVariants.length || !config.intent_workflow_id,
      },
    );

  const versionHistory = useMemo(
    () => allVersionHistories?.[config.intent_workflow_id ?? ''] ?? [],
    [allVersionHistories, config.intent_workflow_id],
  );

  if (!validVariants.length) {
    return null;
  }

  return (
    <Box
      sx={{
        alignSelf: 'flex-start',
        backgroundColor: palette.colors.white,
        border: `1px solid ${palette.colors.slate[200]}`,
        borderRadius: '8px',
        flexShrink: 0,
        padding: '24px',
        position: 'sticky',
        top: 34,
        width: 402,
      }}
    >
      {/* Summary Section */}
      <Typography variant='font12Medium'>
        {t('sidebar.summaryHeading')}
      </Typography>

      <Box
        sx={{
          background: SUMMARY_CARD_GRADIENT,
          border: `1px solid ${palette.colors.slate[100]}`,
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          mt: '10px',
          padding: '16px',
        }}
      >
        {isLoadingSummaries
          ? validVariants.map((_, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <Skeleton height={24} variant='rectangular' width='60%' />
                <Skeleton height={60} variant='rectangular' width='100%' />
              </Box>
            ))
          : validVariants.map((variant, index) => {
              const summary = summaries.find(
                s =>
                  s.variant_name === variant.name &&
                  s.intent_workflow_version === variant.intent_workflow_version,
              );

              return (
                <Box
                  key={`${variant.name}-${variant.intent_workflow_version}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: '4px',
                    }}
                  >
                    <Chip
                      color={palette.colors.white}
                      fontSize='11px'
                      label={variant.name ?? ''}
                      size='small'
                      sx={{
                        backgroundColor: getBadgeColor(variant, index, {
                          control: palette.colors.teal[500],
                          variantA: palette.chart[1],
                          variantB: palette.chart[2],
                        }),
                        fontWeight: 600,
                        lineHeight: '14px',
                        width: 'fit-content',
                      }}
                      variant='filled'
                    />
                    <Typography variant='font14Medium'>
                      {getVariantVersionLabel(variant, versionHistory)}
                    </Typography>
                  </Box>

                  <Typography color='#162040' variant='font14'>
                    {summary?.summary ?? t('sidebar.noSummaryAvailable')}
                  </Typography>
                  <Link
                    href={getVersionLink(
                      variant,
                      config.intent_workflow_id,
                      config.product,
                    )}
                    rel='noopener noreferrer'
                    sx={{
                      color: palette.colors.teal[500],
                      fontSize: '14px',
                      fontWeight: 600,
                      textDecoration: 'underline',
                    }}
                    target='_blank'
                  >
                    {t('sidebar.viewVersion')}
                  </Link>
                </Box>
              );
            })}
      </Box>

      {/* Recommendations Section - only in draft mode */}
      {config.status === 'draft' && summaries.length > 0 && (
        <Box sx={{ mt: '24px' }}>
          <RecommendationsSection
            abTestConfigId={config.ab_test_config_id}
            config={config}
            variantSummaries={summaries}
          />
        </Box>
      )}
    </Box>
  );
};

export default ABTestSidebar;
